/**
 * Warns on page unload while upload batches are in flight. Queued File blobs
 * live only in memory, so reloading or closing the page mid-upload loses them
 * permanently. Browsers ignore custom beforeunload text and show their own
 * generic "leave site?" dialog — the point is the confirmation step, not the
 * message; the upload progress panel provides the context.
 *
 * This is deliberately NOT a React hook: dropzone panes render inside
 * React.Activity, which destroys effects on every tab hide while uploads keep
 * running in the background. An effect-based guard (e.g. react-use's
 * useBeforeUnload) would unregister exactly when the user switches tabs
 * mid-upload. A module-level listener scoped to the batch promise survives
 * hide/show cycles and unregisters when the last batch settles.
 */

let inFlightBatchCount = 0;

export async function withUploadUnloadGuard<T>(run: () => Promise<T>): Promise<T> {
  inFlightBatchCount += 1;
  if (inFlightBatchCount === 1) window.addEventListener('beforeunload', warnBeforeUnload);
  try {
    return await run();
  } finally {
    inFlightBatchCount -= 1;
    if (inFlightBatchCount === 0) window.removeEventListener('beforeunload', warnBeforeUnload);
  }
}

function warnBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  // Chrome only shows the confirmation dialog when returnValue is set.
  event.returnValue = '';
}
